// client/src/components/secure/DashboardLayout.js
import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { isExaminer, isAgentLogin, hasJobProfile, pageBlockedFor, visibleSections, filterSections, activeSectionTitle, readNavOpen, writeNavOpen, isSectionOpen, applyNavOrder, moveSection, readNavOrder, writeNavOrder } from './navConfig';
import { useApiClient } from '../../hooks/useApiClient';
import { InstallAppButton } from '../shared/InstallApp';
import useSiteBrand, { SiteBar } from '../../hooks/useSiteBrand';

// Navigation is data (navConfig.js): seven collapsible sections in a fixed order, each item
// gated by the same `show(user)` predicates as before. The section holding the current page
// opens by itself; open/closed choices are remembered in this browser. See docs/ui-tidy.md.
// "Arrange menu" (bottom of the sidebar) lets each login move whole sections up, down, to the
// top or to the bottom; the order is saved to the login (/api/ui-prefs) and this browser.
const Chevron = ({ open }) => (
    <svg
        className={`w-4 h-4 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
        viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
    >
        <path fillRule="evenodd" d="M7.05 4.55a1 1 0 011.4 0l4.5 4.5a1 1 0 010 1.4l-4.5 4.5a1 1 0 11-1.4-1.4L10.8 10 7.05 6.25a1 1 0 010-1.4z" clipRule="evenodd" />
    </svg>
);

// Consistent styling for every leaf link: one calm resting color, a clear hover,
// and a single accent for the active route (no more mixed colors).
const linkClass = ({ isActive }) =>
    [
        'block rounded-md px-3 py-2.5 lg:py-2 text-sm transition-colors',
        isActive
            ? 'bg-slate-800 text-white font-medium'
            : 'text-slate-200 hover:bg-slate-700/70 hover:text-white',
    ].join(' ');

// NavLink matches the pathname only, so items that differ by ?tab= would all light up
// together. An item whose `to` carries a query is active only when that query matches;
// an item marked `noTab` is active only when there is no ?tab= at all.
const NavItem = ({ item, indent = false }) => {
    const location = useLocation();
    const [, query] = item.to.split('?');
    const tab = new URLSearchParams(location.search).get('tab');
    const queryOk = query
        ? new URLSearchParams(query).get('tab') === tab
        : !(item.noTab && tab);
    return (
        <NavLink to={item.to} end={item.end} style={indent ? { paddingLeft: '1.5rem' } : undefined}
            className={({ isActive }) => linkClass({ isActive: isActive && queryOk })}>
            {item.label}
        </NavLink>
    );
};

const safeStorage = () => { try { return window.localStorage; } catch (e) { return null; } };

const arrangeBtn = 'px-1.5 py-1 rounded text-slate-300 hover:text-white hover:bg-slate-600 disabled:opacity-30 disabled:hover:bg-transparent';

// While arranging, each section is one row: its title and four move buttons.
const ArrangeRow = ({ title, first, last, onMove }) => (
    <div className="mt-1 flex items-center justify-between gap-1 px-3 py-1.5 rounded-md bg-slate-700/50">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-200 truncate">{title}</span>
        <span className="flex items-center flex-shrink-0 text-sm">
            <button type="button" className={arrangeBtn} disabled={first} onClick={() => onMove('top')} aria-label={`Move ${title} to the top`} title="Move to the top">⤒</button>
            <button type="button" className={arrangeBtn} disabled={first} onClick={() => onMove('up')} aria-label={`Move ${title} up`} title="Move up">▲</button>
            <button type="button" className={arrangeBtn} disabled={last} onClick={() => onMove('down')} aria-label={`Move ${title} down`} title="Move down">▼</button>
            <button type="button" className={arrangeBtn} disabled={last} onClick={() => onMove('bottom')} aria-label={`Move ${title} to the bottom`} title="Move to the bottom">⤓</button>
        </span>
    </div>
);

const NavSection = ({ section, open, onToggle, forceOpen }) => {
    const shown = forceOpen || open;
    const id = `nav-sec-${section.title.replace(/\W+/g, '-').toLowerCase()}`;
    let lastSub = null;
    return (
        <div className="mt-1">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={shown}
                aria-controls={id}
                disabled={forceOpen}
                className="w-full flex items-center justify-between px-3 py-2.5 lg:py-2 rounded-md text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200 hover:bg-slate-700/40 disabled:hover:bg-transparent"
            >
                <span>{section.title}</span>
                {!forceOpen && <Chevron open={shown} />}
            </button>
            {shown && (
                <div id={id} className="mt-0.5 space-y-0.5">
                    {section.items.map((it) => {
                        const caption = !forceOpen && it.sub && it.sub !== lastSub ? it.sub : null;
                        if (it.sub) lastSub = it.sub;
                        return (
                            <React.Fragment key={it.to}>
                                {caption && <div className="pl-6 pr-3 pt-2 pb-0.5 text-[11px] text-slate-500">{caption}</div>}
                                <NavItem item={it} indent />
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Shown in place of a page an agent login's menu hides (navConfig.pageBlockedFor): a friendly
// notice rather than a page that fails on its first request. The server refuses the data anyway.
// A login with a job profile (navConfig.hasJobProfile) gets the same notice, worded for its job.
const NotForThisLogin = ({ user }) => (
    <div className="max-w-xl mx-auto mt-10 bg-white border border-slate-200 rounded-lg p-6 text-center shadow-sm">
        <h1 className="text-xl font-bold text-slate-800">Not available for your login</h1>
        <p className="mt-2 text-sm text-slate-600">
            {hasJobProfile(user) && !isAgentLogin(user)
                ? 'This page is not part of your job access. Your menu shows everything your job uses. If you need this page, ask the managing broker to add it on the Job Access screen.'
                : 'This page is part of the office\'s administration. Your login opens your own clients, deals and listings, and the property and zoning searches. If you need something from this page, ask the managing broker.'}
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
            <NavLink to="/dashboard" className="px-4 py-2 text-sm rounded-md bg-slate-800 text-white hover:bg-slate-900">Go to my home</NavLink>
            {(!hasJobProfile(user) || user.areas.includes('property_search')) && (
                <NavLink to="/dashboard/property-search" className="px-4 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50">Property search</NavLink>
            )}
        </div>
    </div>
);

const DashboardLayout = ({ user, onLogout }) => {
    const location = useLocation();
    // Remembered open/closed sections (localStorage; any failure just means "not remembered").
    const [openState, setOpenState] = React.useState(() => readNavOpen(safeStorage()));
    const [navQuery, setNavQuery] = React.useState('');
    // Set by the sign-in step when a recovery code was used (how many are left).
    const [tfNotice, setTfNotice] = React.useState(() => {
        try { return sessionStorage.getItem('twoFactorNotice') || ''; } catch (e) { return ''; }
    });
    const dismissTfNotice = () => {
        try { sessionStorage.removeItem('twoFactorNotice'); } catch (e) { /* ignore */ }
        setTfNotice('');
    };

    const examiner = isExaminer(user);
    // Section order: this browser's copy at once, then the login's saved order from the server.
    const api = useApiClient();
    const [navOrder, setNavOrder] = React.useState(() => readNavOrder(safeStorage()));
    const [arranging, setArranging] = React.useState(false);
    const [orderNote, setOrderNote] = React.useState('');
    React.useEffect(() => {
        let live = true;
        api.get('/api/ui-prefs/nav-order').then((r) => {
            if (!live || !r || r.saved === false || !Array.isArray(r.order)) return;
            setNavOrder(r.order);
            writeNavOrder(r.order, safeStorage());
        }).catch(() => { /* keep this browser's copy */ });
        return () => { live = false; };
    }, [api]);
    const saveOrder = (order) => {
        setNavOrder(order);
        writeNavOrder(order, safeStorage());
        api.put('/api/ui-prefs/nav-order', { order })
            .then(() => setOrderNote(''))
            .catch(() => setOrderNote('Saved in this browser only (could not reach the server).'));
    };
    const sections = React.useMemo(() => applyNavOrder(visibleSections(user), navOrder), [user, navOrder]);
    const moveSec = (title, where) => saveOrder(moveSection(sections.map((s) => s.title), title, where));
    const shownSections = React.useMemo(() => filterSections(sections, navQuery), [sections, navQuery]);
    const searching = navQuery.trim() !== '';

    // The section holding the current page opens when you arrive on it (you can still close it).
    const activeTitle = React.useMemo(
        () => activeSectionTitle(sections, location.pathname, location.search),
        [sections, location.pathname, location.search]);
    React.useEffect(() => {
        if (!activeTitle) return;
        setOpenState((s) => (s[activeTitle] === true ? s : { ...s, [activeTitle]: true }));
    }, [activeTitle]);
    React.useEffect(() => { writeNavOpen(openState, safeStorage()); }, [openState]);
    const toggleSection = (title) => setOpenState((s) => ({ ...s, [title]: !isSectionOpen(title, s, activeTitle) }));
    React.useEffect(() => { setNavQuery(''); }, [location.pathname, location.search]);

    // Phones and tablets (below the lg breakpoint): the sidebar is a drawer behind the
    // menu button, closed again on every navigation. Desktop: always shown, as before.
    const [navOpen, setNavOpen] = React.useState(false);
    // Florida: a yellow "Florida" bar on top and a yellow site name; Illinois unchanged.
    const site = useSiteBrand();
    React.useEffect(() => { setNavOpen(false); }, [location.pathname, location.search]);
    React.useEffect(() => {
        if (!navOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [navOpen]);

    return (
        <div className="flex h-screen h-[100dvh] bg-slate-100">
            {/* Dim backdrop behind the open drawer (small screens only). */}
            {navOpen && (
                <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" aria-hidden="true" onClick={() => setNavOpen(false)} />
            )}
            {/* Sidebar Navigation */}
            <aside id="dashboard-nav"
                className={`fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] transform transition-transform duration-200 ${navOpen ? 'translate-x-0' : '-translate-x-full'} lg:static lg:z-auto lg:w-64 lg:max-w-none lg:translate-x-0 flex-shrink-0 bg-slate-800 text-slate-300 flex flex-col`}
                style={{ paddingTop: 'env(safe-area-inset-top)' }}>
                <div className={`h-16 flex items-center justify-between lg:justify-center px-4 flex-shrink-0 ${site ? site.accentClass : 'bg-slate-900'}`}>
                    <h1 className={`text-xl font-bold ${site ? '' : 'text-white'}`}>{site ? site.siteName : 'BREC Portal'}</h1>
                    <button type="button" onClick={() => setNavOpen(false)} className={`lg:hidden p-2 -mr-2 ${site ? 'text-slate-800 hover:text-black' : 'text-slate-300 hover:text-white'}`} aria-label="Close menu">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" /></svg>
                    </button>
                </div>
                {examiner && (
                    <div className="mx-2 mt-3 px-3 py-2 rounded-md bg-amber-500/15 border border-amber-500/40">
                        <div className="text-amber-300 text-xs font-semibold uppercase tracking-wide">Inspection access</div>
                        <div className="text-amber-100/80 text-xs mt-0.5">Read-only, and limited to the areas the managing broker has granted. See <span className="underline">My Access</span>.</div>
                    </div>
                )}
                {!arranging && <div className="px-2 pt-3">
                    <label htmlFor="nav-search" className="sr-only">Search the menu</label>
                    <input
                        id="nav-search"
                        type="search"
                        value={navQuery}
                        onChange={(e) => setNavQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape' && navQuery) { e.stopPropagation(); setNavQuery(''); } }}
                        placeholder="Search the menu"
                        autoComplete="off"
                        className="w-full rounded-md bg-slate-700/60 border border-slate-600 px-3 py-2 text-sm text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                </div>}
                <nav className="flex-grow px-2 py-3 overflow-y-auto overscroll-contain" aria-label="Portal menu">
                    {arranging && (
                        <>
                            <p className="px-3 pb-1 text-xs text-slate-400">Move each section with the arrows. Your order is saved to your login, on every device.</p>
                            {sections.map((sec, i) => (
                                <ArrangeRow key={sec.title} title={sec.title} first={i === 0} last={i === sections.length - 1}
                                    onMove={(where) => moveSec(sec.title, where)} />
                            ))}
                        </>
                    )}
                    {!arranging && shownSections.map((sec) => (
                        <NavSection
                            key={sec.title}
                            section={sec}
                            open={isSectionOpen(sec.title, openState, activeTitle)}
                            onToggle={() => toggleSection(sec.title)}
                            forceOpen={searching}
                        />
                    ))}
                    {!arranging && searching && shownSections.length === 0 && (
                        <p className="px-3 py-2 text-sm text-slate-400">No menu items match "{navQuery}".</p>
                    )}
                </nav>
                <div className="px-2 py-2 border-t border-slate-700 flex-shrink-0 flex items-center gap-2 text-xs" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
                    {arranging ? (
                        <>
                            <button type="button" onClick={() => setArranging(false)} className="px-3 py-1.5 rounded-md bg-slate-600 text-white hover:bg-slate-500">Done</button>
                            <button type="button" onClick={() => { if (window.confirm('Put the menu sections back in the standard order?')) saveOrder([]); }}
                                className="px-2 py-1.5 rounded-md text-slate-300 hover:text-white hover:bg-slate-700">Reset to standard order</button>
                        </>
                    ) : (
                        <button type="button" onClick={() => { setNavQuery(''); setArranging(true); }} className="px-2 py-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700">Arrange menu</button>
                    )}
                </div>
                {orderNote && <p className="px-3 pb-2 text-xs text-amber-300">{orderNote}</p>}
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                <SiteBar brand={site} style={{ paddingTop: 'env(safe-area-inset-top)' }} />
                <header className="bg-white shadow-sm z-10" style={site ? undefined : { paddingTop: 'env(safe-area-inset-top)' }}>
                    <div className="flex items-center gap-2 h-14 sm:h-16 px-3 sm:px-6">
                        <button type="button" onClick={() => setNavOpen(true)} className="lg:hidden p-2 -ml-1 rounded-md text-slate-700 hover:bg-slate-100"
                            aria-label="Open menu" aria-controls="dashboard-nav" aria-expanded={navOpen}>
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" /></svg>
                        </button>
                        {site
                            ? <span className={`lg:hidden font-bold rounded px-1.5 ${site.accentClass}`}>{site.shortName}</span>
                            : <span className="lg:hidden font-bold text-slate-800">BREC</span>}
                        <div className="ml-auto flex items-center gap-2 sm:gap-4 min-w-0">
                            <InstallAppButton />
                            <span className="hidden sm:inline text-sm text-gray-600 truncate">Welcome, {user?.username || 'User'}!</span>
                            <button onClick={onLogout} className="px-3 sm:px-4 py-2 min-h-[40px] text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 flex-shrink-0">
                                Logout
                            </button>
                        </div>
                    </div>
                </header>
                <main className="brec-dash-main flex-1 min-w-0 overflow-x-hidden overflow-y-auto bg-slate-100 p-3 sm:p-6" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
                    {tfNotice && (
                        <div className="mb-4 px-3 py-2 rounded border border-amber-200 bg-amber-50 text-sm text-amber-900 flex justify-between gap-3">
                            <span>{tfNotice} <NavLink to="/dashboard/security" className="underline">My Security</NavLink></span>
                            <button onClick={dismissTfNotice} className="text-amber-700 hover:underline">Dismiss</button>
                        </div>
                    )}
                    {pageBlockedFor(user, location.pathname) ? <NotForThisLogin user={user} /> : <Outlet />}
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;
